import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useMsal } from '@azure/msal-react';
import {
  EventType,
  InteractionRequiredAuthError,
  type AccountInfo,
  type AuthenticationResult,
  type EventMessage,
} from '@azure/msal-browser';
import { apiRequest, loginRequest } from '../authConfig';
import {
  registerTokenGetter,
  setActiveAccountKey,
  apiCall,
  ApiError,
  invalidateApiCache,
} from '../utils/apiHelpers';

export interface UserInfo {
  actorId: string;
  email: string;
  displayName: string;
  platformAdmin: boolean;
  allowedIndustries: { industryId: string; role: 'owner' | 'editor' | 'viewer' }[];
}

export interface AuthContextValue {
  userInfo: UserInfo | null;
  tokenReady: boolean;
  isAuthenticated: boolean;
  authError: string | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const { instance } = useMsal();
  const [activeAccount, setActiveAccount] = useState<AccountInfo | null>(() => {
    const explicit = instance.getActiveAccount();
    if (explicit) return explicit;
    const cached = instance.getAllAccounts();
    const first = cached[0];
    if (first) {
      instance.setActiveAccount(first);
      return first;
    }
    return null;
  });
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [tokenReady, setTokenReady] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);

  useEffect(() => {
    const cb = instance.addEventCallback((event: EventMessage) => {
      switch (event.eventType) {
        case EventType.LOGIN_SUCCESS:
        case EventType.ACQUIRE_TOKEN_SUCCESS: {
          const result = event.payload as AuthenticationResult | undefined;
          if (result?.account) {
            instance.setActiveAccount(result.account);
            setActiveAccount(result.account);
          }
          break;
        }
        case EventType.LOGOUT_SUCCESS:
        case EventType.ACCOUNT_REMOVED:
          setActiveAccount(null);
          break;
        case EventType.ACCOUNT_ADDED: {
          const current = instance.getActiveAccount();
          if (current) setActiveAccount(current);
          break;
        }
        default:
          break;
      }
    });
    return () => {
      if (cb) instance.removeEventCallback(cb);
    };
  }, [instance]);

  const acquireToken = useCallback(async (): Promise<string | null> => {
    const account = instance.getActiveAccount() ?? activeAccount;
    if (!account) return null;
    try {
      const result = await instance.acquireTokenSilent({ ...apiRequest, account });
      // Use the ID token, not the access token. With OIDC-only scopes
      // (openid/profile/email) MSAL fills `accessToken` with a token for
      // Microsoft Graph (aud: 00000003-0000-0000-c000-000000000000),
      // which our Function App Easy Auth correctly rejects. The ID token
      // has the right audience (the SPA app reg client ID — what Easy Auth
      // is configured to validate). Same pattern docreview uses.
      return result.idToken || result.accessToken;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        // Do NOT pass the cached `account`. Its username is External
        // ID's synthetic `<objectId>@bacrexternalauth.onmicrosoft.com`
        // UPN; MSAL would forward that as `login_hint` and External
        // ID would match the local shadow record and ask for a
        // password the federated (Google) user doesn't have. Same
        // reasoning as the 401-retry branch in the effect below.
        // Mirror of the same fix in docreview's AuthContext.tsx.
        await instance.acquireTokenRedirect({ ...apiRequest, prompt: 'select_account' });
        return null;
      }
      throw err;
    }
  }, [activeAccount, instance]);

  useEffect(() => {
    registerTokenGetter(acquireToken);
  }, [acquireToken]);

  useEffect(() => {
    setActiveAccountKey(activeAccount?.homeAccountId ?? null);
  }, [activeAccount]);

  const accountKey = activeAccount?.homeAccountId ?? null;
  useEffect(() => {
    if (!accountKey) {
      setUserInfo(null);
      setTokenReady(false);
      setAuthError(null);
      invalidateApiCache();
      return;
    }
    let cancelled = false;
    setTokenReady(false);
    setAuthError(null);
    (async () => {
      try {
        const me = await apiCall<UserInfo>('/api/me');
        if (cancelled) return;
        setUserInfo(me);
        sessionStorage.removeItem(`qrplatform.authRetry::${accountKey ?? ''}`);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError) {
          if (err.status === 401) {
            const RETRY_KEY = `qrplatform.authRetry::${accountKey ?? ''}`;
            const alreadyRetried = sessionStorage.getItem(RETRY_KEY) === '1';
            if (alreadyRetried) {
              sessionStorage.removeItem(RETRY_KEY);
              setAuthError(
                'Authentication keeps failing. Sign out completely (incognito window helps) and try again. If this persists, contact your administrator.',
              );
            } else {
              sessionStorage.setItem(RETRY_KEY, '1');
              // Do NOT pass the cached `account` here. Its `username` is
              // External ID's synthetic
              // `<objectId>@bacrexternalauth.onmicrosoft.com` UPN, which
              // MSAL forwards as `login_hint` — External ID then matches
              // the local shadow record and asks for a local-account
              // password that federated (Google) users don't have.
              // Explicit `prompt: 'select_account'` here (and on
              // `loginRequest` for the `loginRedirect` fallback below)
              // forces the IdP picker so the user can re-pick Google.
              // Mirror of the same fix in docreview's AuthContext.tsx.
              try {
                await instance.acquireTokenRedirect({ ...apiRequest, prompt: 'select_account' });
                return;
              } catch (e) {
                console.error('AuthContext: acquireTokenRedirect failed', e);
                try {
                  await instance.loginRedirect(loginRequest);
                  return;
                } catch (innerErr) {
                  console.error('AuthContext: loginRedirect failed', innerErr);
                }
              }
              setAuthError('Authentication failed. Sign in again.');
            }
          } else if (err.status === 403) {
            setAuthError('Your account is not authorized to access this app. Contact your administrator.');
          } else {
            setAuthError(err.message);
          }
        } else {
          setAuthError((err as Error).message);
        }
        setUserInfo(null);
      } finally {
        if (!cancelled) setTokenReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accountKey, instance]);

  const login = useCallback(async () => {
    await instance.loginRedirect(loginRequest);
  }, [instance]);

  const logout = useCallback(async () => {
    await instance.logoutRedirect();
  }, [instance]);

  const refresh = useCallback(async () => {
    try {
      const me = await apiCall<UserInfo>('/api/me', { skipCache: true });
      setUserInfo(me);
    } catch (err) {
      // best-effort post-mutation refresh; existing userInfo stays valid
      // because the user is still authenticated. Failed refresh just means
      // stale display fields until next sign-in. Log for diagnosability.
      console.warn('AuthContext: /me refresh failed', err);
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      userInfo,
      tokenReady,
      refresh,
      isAuthenticated: !!activeAccount,
      authError,
      login,
      logout,
    }),
    [userInfo, tokenReady, activeAccount, authError, login, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
